import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { collection, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Teacher {
  teacherId: string;
  name: string;
  email: string;
  phone: string;
  department: string;
  subject: string;
  qualification: string;
  experience: number;
  joiningDate: string;
  salary: number;
  address: string;
  emergencyContact: string;
  aadhaarNumber?: string;
  bankAccountNumber?: string;
  bankIFSC?: string;
  bankName?: string;
}

interface ValidationError {
  row: number;
  message: string;
}

interface BulkUploadTeachersProps {
  domain: string;
}

const BulkUploadTeachers: React.FC<BulkUploadTeachersProps> = ({ domain }) => {
  const [file, setFile] = useState<File | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);

  const downloadTemplate = () => {
    const templateData = [
      {
        'Teacher ID': '17PVSS0001',
        'Name': 'John Doe',
        'Email': 'john.doe@school.com',
        'Phone': '9876543210',
        'Department': 'Science',
        'Subject': 'Physics',
        'Qualification': 'M.Sc. Physics',
        'Experience (Years)': 5,
        'Joining Date': '2020-01-15',
        'Salary': 50000,
        'Address': '123 Main Street, City',
        'Emergency Contact': '9876543211',
        'Aadhaar Number': '123456789012',
        'Bank Account Number': '1234567890123456',
        'Bank IFSC Code': 'SBIN0001234',
        'Bank Name': 'State Bank of India',
      },
      {
        'Teacher ID': '17PVSS0002',
        'Name': 'Jane Smith',
        'Email': 'jane.smith@school.com',
        'Phone': '9876543212',
        'Department': 'Mathematics',
        'Subject': 'Mathematics',
        'Qualification': 'M.Sc. Mathematics',
        'Experience (Years)': 8,
        'Joining Date': '2018-06-01',
        'Salary': 55000,
        'Address': '456 Park Avenue, City',
        'Emergency Contact': '9876543213',
        'Aadhaar Number': '987654321098',
        'Bank Account Number': '9876543210987654',
        'Bank IFSC Code': 'HDFC0001234',
        'Bank Name': 'HDFC Bank',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Teachers');

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // Teacher ID
      { wch: 20 }, // Name
      { wch: 25 }, // Email
      { wch: 15 }, // Phone
      { wch: 15 }, // Department
      { wch: 15 }, // Subject
      { wch: 20 }, // Qualification
      { wch: 18 }, // Experience
      { wch: 15 }, // Joining Date
      { wch: 12 }, // Salary
      { wch: 30 }, // Address
      { wch: 15 }, // Emergency Contact
      { wch: 18 }, // Aadhaar Number
      { wch: 20 }, // Bank Account Number
      { wch: 15 }, // Bank IFSC Code
      { wch: 25 }, // Bank Name
    ];

    XLSX.writeFile(wb, 'teacher_bulk_upload_template.xlsx');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setTeachers([]);
      setValidationErrors([]);
      setUploadComplete(false);
      parseExcel(selectedFile);
    }
  };

  const parseExcel = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const parsedTeachers: Teacher[] = [];
        const errors: ValidationError[] = [];

        jsonData.forEach((row: any, index: number) => {
          const teacherId = String(row['Teacher ID'] || '').trim();
          const name = String(row['Name'] || '').trim();
          const email = String(row['Email'] || '').trim();
          const phone = String(row['Phone'] || '').trim();
          const department = String(row['Department'] || '').trim();
          const subject = String(row['Subject'] || '').trim();
          const qualification = String(row['Qualification'] || '').trim();
          const experience = Number(row['Experience (Years)'] || 0);
          const joiningDate = String(row['Joining Date'] || '').trim();
          const salary = Number(row['Salary'] || 0);
          const address = String(row['Address'] || '').trim();
          const emergencyContact = String(row['Emergency Contact'] || '').trim();
          const aadhaarNumber = row['Aadhaar Number'] ? String(row['Aadhaar Number']).trim() : undefined;
          const bankAccountNumber = row['Bank Account Number'] ? String(row['Bank Account Number']).trim() : undefined;
          const bankIFSC = row['Bank IFSC Code'] ? String(row['Bank IFSC Code']).trim() : undefined;
          const bankName = row['Bank Name'] ? String(row['Bank Name']).trim() : undefined;

          // Validation
          if (!teacherId) {
            errors.push({ row: index + 2, message: 'Teacher ID is required' });
          }
          if (!name) {
            errors.push({ row: index + 2, message: 'Name is required' });
          }
          if (!email) {
            errors.push({ row: index + 2, message: 'Email is required' });
          }
          if (!phone) {
            errors.push({ row: index + 2, message: 'Phone is required' });
          }

          if (teacherId && name && email && phone) {
            parsedTeachers.push({
              teacherId,
              name,
              email,
              phone,
              department,
              subject,
              qualification,
              experience,
              joiningDate,
              salary,
              address,
              emergencyContact,
              aadhaarNumber,
              bankAccountNumber,
              bankIFSC,
              bankName,
            });
          }
        });

        setTeachers(parsedTeachers);
        setValidationErrors(errors);

        if (errors.length > 0) {
          console.warn('Validation errors found:', errors);
        } else {
          console.log('All teachers validated successfully');
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setValidationErrors([{ row: 0, message: 'Failed to parse Excel file. Please check the format.' }]);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const uploadTeachers = async () => {
    if (validationErrors.length > 0) {
      alert('Please fix validation errors before uploading');
      return;
    }

    if (teachers.length === 0) {
      alert('No teachers to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const batchSize = 500;
      const batches = [];

      for (let i = 0; i < teachers.length; i += batchSize) {
        batches.push(teachers.slice(i, i + batchSize));
      }

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = writeBatch(db);
        const currentBatch = batches[batchIndex];

        currentBatch.forEach((teacher) => {
          const docId = teacher.teacherId.replace(/\s+/g, '_');
          const docRef = doc(collection(db, `school-users/${domain}/teachers`), docId);

          const teacherData = {
            ...teacher,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            status: 'active',
          };

          batch.set(docRef, teacherData);
        });

        await batch.commit();

        // Update progress
        const progress = ((batchIndex + 1) / batches.length) * 100;
        setUploadProgress(progress);
      }

      setUploadComplete(true);
      console.log(`✅ Successfully uploaded ${teachers.length} teachers`);
    } catch (error: any) {
      console.error('Error uploading teachers:', error);
      alert(`Failed to upload teachers: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setTeachers([]);
    setValidationErrors([]);
    setUploadComplete(false);
    setUploadProgress(0);
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-cloud-upload me-2 text-primary-custom"></i>
          Bulk Upload Teacher Profiles
        </h5>
        <small className="text-muted">Upload multiple teacher records using Excel file</small>
      </div>
      <div className="card-body">
        {/* Download Template */}
        <div className="alert alert-info d-flex align-items-center mb-4">
          <i className="bi bi-info-circle fs-4 me-3"></i>
          <div className="flex-grow-1">
            <strong>Step 1:</strong> Download the Excel template, fill in teacher details, and upload.
            <br />
            <small>Template includes sample data to guide you.</small>
          </div>
          <button className="btn btn-primary" onClick={downloadTemplate}>
            <i className="bi bi-download me-2"></i>
            Download Template
          </button>
        </div>

        {/* Instructions */}
        <div className="alert alert-warning mb-4">
          <h6 className="alert-heading">
            <i className="bi bi-lightbulb me-2"></i>
            Required Fields
          </h6>
          <ul className="mb-0 small">
            <li><strong>Teacher ID:</strong> Unique identifier (e.g., 17PVSS0001)</li>
            <li><strong>Name:</strong> Full name of the teacher</li>
            <li><strong>Email:</strong> Valid email address</li>
            <li><strong>Phone:</strong> Contact number (10 digits)</li>
            <li><strong>Optional:</strong> Department, Subject, Qualification, Experience, Joining Date, Salary, Address, Emergency Contact, Aadhaar Number, Bank Account Number, Bank IFSC Code, Bank Name</li>
          </ul>
        </div>

        {/* File Upload */}
        <div className="mb-4">
          <label className="form-label fw-semibold">
            <i className="bi bi-file-earmark-excel me-2"></i>
            Step 2: Upload Excel File
          </label>
          <input
            type="file"
            className="form-control"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={uploading}
          />
          {file && (
            <small className="text-muted d-block mt-2">
              <i className="bi bi-check-circle text-success me-1"></i>
              Selected: {file.name}
            </small>
          )}
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="alert alert-danger">
            <h6 className="alert-heading">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Validation Errors ({validationErrors.length})
            </h6>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {validationErrors.map((error, idx) => (
                    <tr key={idx}>
                      <td>{error.row}</td>
                      <td>{error.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Preview */}
        {teachers.length > 0 && validationErrors.length === 0 && (
          <div className="mb-4">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-eye me-2 text-success"></i>
              Preview ({teachers.length} teachers)
            </h6>
            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table className="table table-sm table-bordered">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Teacher ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Department</th>
                    <th>Subject</th>
                    <th>Aadhaar</th>
                    <th>Bank Account</th>
                    <th>IFSC</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.map((teacher, idx) => (
                    <tr key={idx}>
                      <td>{teacher.teacherId}</td>
                      <td>{teacher.name}</td>
                      <td>{teacher.email}</td>
                      <td>{teacher.phone}</td>
                      <td>{teacher.department}</td>
                      <td>{teacher.subject}</td>
                      <td>{teacher.aadhaarNumber || '-'}</td>
                      <td>{teacher.bankAccountNumber || '-'}</td>
                      <td>{teacher.bankIFSC || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="mb-4">
            <div className="d-flex justify-content-between mb-2">
              <span>Uploading teachers...</span>
              <span>{Math.round(uploadProgress)}%</span>
            </div>
            <div className="progress" style={{ height: '25px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${uploadProgress}%` }}
              >
                {Math.round(uploadProgress)}%
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {uploadComplete && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle me-2"></i>
            Successfully uploaded {teachers.length} teacher profiles to <strong>{domain}</strong>!
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-flex gap-2">
          {!uploadComplete && teachers.length > 0 && validationErrors.length === 0 && (
            <button
              className="btn btn-success"
              onClick={uploadTeachers}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-2"></i>
                  Upload {teachers.length} Teachers
                </>
              )}
            </button>
          )}
          {(teachers.length > 0 || uploadComplete) && (
            <button className="btn btn-secondary" onClick={reset} disabled={uploading}>
              <i className="bi bi-arrow-counterclockwise me-2"></i>
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadTeachers;
